import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BaseUrl = process.env.REACT_APP_BASH_URL;
const API_URL = `${BaseUrl}/admin/blogs/categories`;

// Async Thunks
export const fetchCategories = createAsyncThunk('categories/fetchCategories', async () => {
  const token = localStorage.getItem("admin-auth-token");
  const response = await axios.get(`${API_URL}`, { 
    headers: { Authorization: `Bearer ${token}` } 
  });
  return response.data;
});

export const deleteCategories = createAsyncThunk('categories/deleteCategories', async (ids) => {
  const token = localStorage.getItem("admin-auth-token");
  const response = await axios.post(`${API_URL}/delete`, { ids }, { 
    headers: { Authorization: `Bearer ${token}` } 
  });
  return ids;
});

export const createCategory = createAsyncThunk('categories/create', async (data) => {
  const token = localStorage.getItem("admin-auth-token");
  const response = await axios.post(`${API_URL}`, data, { 
    headers: { Authorization: `Bearer ${token}` } 
  });
  return response.data;
});

export const updateCategory = createAsyncThunk('categories/update', async ({ id, data }) => {
  const token = localStorage.getItem("admin-auth-token");
  const response = await axios.put(`${API_URL}/${id}`, data, { 
    headers: { Authorization: `Bearer ${token}` } 
  });
  return response.data;
});

export const editCategories = createAsyncThunk('categories/editCategories', async ({ ids, name }) => {
  const token = localStorage.getItem("admin-auth-token");
  const response = await axios.post(`${API_URL}/edit`, { ids, name }, { 
    headers: { Authorization: `Bearer ${token}` } 
  });
  return { ids, updatedName: name };
});

// Slice
const categoriesSlice = createSlice({
  name: 'blogCategories',
  initialState: {
    categories: [],
    flatCategories: [],
    loading: false,
    error: null,
  },
  reducers: {
    toggleCategorySelection: (state, action) => {
      const category = state.flatCategories.find(c => c._id === action.payload);
      if (category) category.selected = !category.selected;
    },
    toggleAllSelection: (state, action) => {
      state.flatCategories.forEach(category => {
        category.selected = action.payload;
      });
    },
    clearSelected: (state) => {
      state.flatCategories.forEach(category => {
        category.selected = false;
      });
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
        state.flatCategories = flattenCategoryTree(action.payload);
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = [...state.categories, action.payload.category];
        state.flatCategories = flattenCategoryTree(state.categories);
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.categories.findIndex(c => c._id === action.payload.category._id);
        if (index !== -1) {
          state.categories[index] = action.payload.category;
          state.flatCategories = flattenCategoryTree(state.categories);
        }
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteCategories.fulfilled, (state, action) => {
        state.categories = state.categories.filter(category => !action.payload.includes(category._id));
        state.flatCategories = flattenCategoryTree(state.categories);
      })
      .addCase(editCategories.fulfilled, (state, action) => {
        state.categories = state.categories.map(category =>
          action.payload.ids.includes(category._id)
            ? { ...category, name: action.payload.updatedName }
            : category
        );
        state.flatCategories = flattenCategoryTree(state.categories);
      });
  },
});

// Helper function to flatten category tree
function flattenCategoryTree(tree, level = 0, result = []) {
  for (const category of tree) {
    result.push({
      ...category,
      level,
      selected: false,
      nameDisplay: `${'— '.repeat(level)}${category.name}`
    });
    if (category.children && category.children.length) {
      flattenCategoryTree(category.children, level + 1, result);
    }
  }
  return result;
}

export const { 
  toggleCategorySelection, 
  toggleAllSelection,
  clearSelected
} = categoriesSlice.actions;
export default categoriesSlice.reducer;